import { useCallback, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

/**
 * Pytanie o porzucenie niezapisanych zmian.
 *
 * „Przeładuj” w danych dla importera VIA i „Zamknij edycję” w prezleceniu
 * kasowały wpisane wartości bez słowa (lista komunikatów z 01.10.2026,
 * „Problemy poza brzmieniem”). Własne okno MUI, nie `window.confirm` —
 * przeglądarkowe okno wygląda jak z innej aplikacji i nie da się go
 * przetestować bez podstawiania `window`.
 */
export const DISCARD_TITLE = 'Porzucić niezapisane zmiany?';
export const DISCARD_TEXT =
  'Wprowadzone zmiany nie zostały zapisane. Jeśli przejdziesz dalej, przepadną.';
export const DISCARD_CONFIRM = 'Porzuć zmiany';
export const DISCARD_CANCEL = 'Wróć do edycji';

export const DiscardChangesDialog = ({
  open,
  onCancel,
  onDiscard,
}: {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => void;
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{DISCARD_TITLE}</DialogTitle>
    <DialogContent>
      <DialogContentText>{DISCARD_TEXT}</DialogContentText>
    </DialogContent>
    <DialogActions>
      {/* Domyślny przycisk to powrót — Enter nie kasuje cudzej pracy. */}
      <Button onClick={onCancel} autoFocus>
        {DISCARD_CANCEL}
      </Button>
      <Button onClick={onDiscard} color="error">
        {DISCARD_CONFIRM}
      </Button>
    </DialogActions>
  </Dialog>
);

/**
 * Strażnik akcji, która porzuca edycję. `guard(action)` wykonuje akcję od
 * razu, gdy zmian nie ma, a przy zmianach najpierw pyta. `dialog` trzeba
 * wyrenderować obok formularza.
 */
export function useDiscardGuard(dirty: boolean): {
  guard: (action: () => void) => void;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<(() => void) | null>(null);

  const guard = useCallback(
    (action: () => void) => {
      if (dirty) setPending(() => action);
      else action();
    },
    [dirty],
  );

  const dialog = (
    <DiscardChangesDialog
      open={pending !== null}
      onCancel={() => setPending(null)}
      onDiscard={() => {
        const action = pending;
        setPending(null);
        action?.();
      }}
    />
  );

  return { guard, dialog };
}

/**
 * Treść formularza do porównania: puste pole (`null`, `undefined`, `''`)
 * znaczy to samo co brak pola, a kolejność kluczy nie gra roli. Inaczej
 * wpisanie i skasowanie znaku albo pole puste w rekordzie, a `''`
 * w formularzu, wyglądałyby na zmianę.
 */
function normalize(v: unknown): unknown {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.map(normalize);
  if (typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(v as object).sort()) {
      const n = normalize((v as Record<string, unknown>)[key]);
      if (n !== null) out[key] = n;
    }
    return out;
  }
  return v;
}

/** Czy dwie wersje danych formularza różnią się treścią. */
export function differs(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalize(a)) !== JSON.stringify(normalize(b));
}
